import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { ToastService, ToastType } from '../../shared/toast/toast.service';
import { CurrentUserService, UserGroupService } from '../../user/services';
import { DataFieldGroup } from '../../shared/datafields/models';
import { LoadingPanelService } from '../../shared/loading-panel/loading-panel.service';
import { ArtifactAssemblyTypes, AssemblyArtifact } from '../../developer/models/artifact';
import { McpServerEntity } from '../models/mcp-server';
import { GridService } from '../../shared/grid/grid.service';
import { McpServerService } from '../services/mcp-server-service';
import { DeveloperArtifactService } from 'src/app/developer/services/developer-artifact.service';
import { SvgIconComponent } from '../../shared/icons/svg-icon.component';
import { PermissionRequiredDirective } from '../../shared/authentication/permission-required.directive';
import { ChangeLogLinkComponent } from '../../shared/change-log/change-log-link/change-log-link.component';
import { ChangeLogLinkEntityDirective } from '../../shared/change-log/change-log-link-entity/change-log-link-entity.directive';
import { DxDataGridModule, DxTextBoxModule } from 'devextreme-angular';
import { BizDataGridDirective } from '../../shared/grid/biz-grid.directive';
import { DxoColumnFixingModule, DxoEditingModule, DxiColumnModule, DxiValidationRuleModule, DxiButtonModule } from 'devextreme-angular/ui/nested';
import { BizUserColumnDirective } from '../../shared/grid/biz-grid-user.directive';
import { BizDateColumnDirective } from '../../shared/grid/biz-grid-date.directive';
import { DxTemplateModule } from 'devextreme-angular/core';

import { DeveloperArtifactAssemblyPickerComponent } from '../../developer/developer-artifacts/developer-artifact-assembly-picker.component';
import { DataFieldGroupContainerComponent } from '../../shared/datafields/datafield-group-container.component';
import { BizGridFiltervalues } from 'src/app/shared/grid/biz-grid-filtervalues';

@Component({
  selector: 'app-mcp-servers',
  standalone: true,
  imports: [SvgIconComponent, PermissionRequiredDirective, ChangeLogLinkComponent, ChangeLogLinkEntityDirective, DxDataGridModule, BizDataGridDirective, DxoColumnFixingModule, DxoEditingModule, DxiColumnModule, DxiValidationRuleModule, DxiButtonModule, BizUserColumnDirective, BizDateColumnDirective, DxTemplateModule, DxTextBoxModule, DeveloperArtifactAssemblyPickerComponent, DataFieldGroupContainerComponent],
  templateUrl: './mcp-servers.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './mcp-servers.component.scss'
})
export class McpServersComponent implements OnInit {

    hasCreatePerm = false;
    hasEditPerm = false;
    hasDeletePerm = false;
    allowShowLog = false;

    mcpServers: McpServerEntity[] = [];
    artifacts: AssemblyArtifact[] = [];
    currentMcpServer: McpServerEntity | null;

    mcpServerDisplayGroups: DataFieldGroup[] = [];
    mcpServerFormGroup: UntypedFormGroup = new UntypedFormGroup({});

    baseApiUrl: string;

    userGroupChoices: {Id: number | null, Name: string}[] = []

    constructor(
        private mcpServerService: McpServerService,
        private toastService: ToastService,
        private currentUserService: CurrentUserService,
        private loadingPanelService: LoadingPanelService,
        private developerArtifactService: DeveloperArtifactService,
        public bizGridFiltervalues: BizGridFiltervalues,
        private gridService: GridService,
        private userGroupService: UserGroupService) {

        this.allowUpdatingRow = this.allowUpdatingRow.bind(this);
        this.allowDeletingRow = this.allowDeletingRow.bind(this);
        this.displayExprArtifact = this.displayExprArtifact.bind(this);
    }

    ngOnInit() {
        void this.ngOnInitAsync();
    }

    async ngOnInitAsync() {
        this.artifacts = await this.developerArtifactService.getAssemblyArtifacts(true, [ArtifactAssemblyTypes.TypeMcpServer]);
        this.hasCreatePerm = this.currentUserService.hasPermission('McpServer_Action_Create');
        this.hasEditPerm = this.currentUserService.hasPermission('McpServer_Action_Edit');
        this.hasDeletePerm = this.currentUserService.hasPermission('McpServer_Action_Delete');
        this.allowShowLog = this.currentUserService.hasPermission('McpServer_Action_ShowLog');
        this.baseApiUrl = await this.mcpServerService.getBaseApiUrl();
        if (this.baseApiUrl.slice(-1) !== '/') {
            this.baseApiUrl = this.baseApiUrl + '/';
        }
        const userGroupsSorted = (await this.userGroupService.getUserGroups()).sort((a, b) => (a.Name > b.Name ? 1 : -1));
        const defaultChoice: {Id: number | null, Name: string} = {Id: null, Name: 'Please choose'};
        this.userGroupChoices = [defaultChoice].concat(userGroupsSorted.map(x => ({Id: x.Id ,Name: x.Name })));
        await this.refresh();
    }

    allowUpdatingRow(options: { component: any; row: any; }): boolean {
        const item: McpServerEntity = options?.row?.data;
        if (item) {
            return this.hasEditPerm;
        }
        return false;
    }

    allowDeletingRow(options: { component: any; row: any; }): boolean {
        const item: McpServerEntity = options?.row?.data;
        if (item) {
            return this.hasDeletePerm;
        }
        return false;
    }

    displayExprArtifact(e: any) {
        if (this.artifacts) {
            var artifact = this.artifacts.find(a => a.ArtifactId == e.value);
            if (artifact) {
                return `${artifact.Name} [${artifact.ClassName}] [${artifact.Filename}]`;
            }
        }
        return 'Unknown...';
    }

    async refresh() {
        this.loadingPanelService.showLoadingPanelDirect('Loading MCP servers...');
        this.mcpServers = await this.mcpServerService.getMcpServers();
        this.mcpServerDisplayGroups = [];
        this.currentMcpServer = null;
        this.loadingPanelService.hideLoadingPanel();
    }

    onInitNewRow(e: any): void {
        e.data = { Id: null, Name: '', Description: '' } as McpServerEntity;
        this.currentMcpServer = e.data;
    }

    onRowInserting = (e: any): void => {
        e.cancel = this.gridService.handleDataGridRow(e.data as McpServerEntity, item => this.saveMcpServer(item));
    };

    onRowUpdating = (e: any): void => {
        e.cancel = this.gridService.handleDataGridRow({ ...e.key, ...e.newData } as McpServerEntity, item => this.saveMcpServer(item), e.newData);
    };

    onRowRemoving = (e: any): void => {
        e.cancel = this.gridService.handleDataGridRow(e.data as McpServerEntity, item => this.removeMcpServer(item));
    };

    async saveMcpServer(mcpServer: McpServerEntity): Promise<McpServerEntity> {
        const mcpServerExist = this.mcpServers.find(i => i.Id != mcpServer.Id && i.UrlKey == mcpServer.UrlKey);
        if (mcpServerExist != null) {
            throw new Error(`Another MCP server with that UrlKey already exists. Name of the existing MCP server: ${mcpServerExist.Name}`);
        }

        const datafieldValues = DataFieldGroup.getDataFieldValues(this.mcpServerDisplayGroups, true);
        const newMcpServer = await this.mcpServerService.saveMcpServer(mcpServer, datafieldValues);
        this.toastService.showToast('MCP server saved...', ToastType.success);
        return newMcpServer;
    }

    async removeMcpServer(mcpServer: McpServerEntity): Promise<McpServerEntity> {
        await this.mcpServerService.deleteMcpServer(mcpServer.Id);
        this.toastService.showToast('MCP server deleted...', ToastType.success);
        return mcpServer;
    }

    async onEditingStart(e: any) {
        const server = e.data as McpServerEntity;
        this.currentMcpServer = server;
        e.component.cellValue(e.component.getRowIndexByKey(e.key), 'Name', e.component.cellValue(e.component.getRowIndexByKey(e.key), 'Name')); // Lidt hacky, men for at vi kan få de dynamiske datafield-værdier med i en save, hvor man ikke har ændret andet end disse værdier, så trigger vi en ændring her, så update altid bliver kaldt.
    }

    async onEditCancelled(e: any) {
        this.currentMcpServer = null;
        this.mcpServerDisplayGroups = [];
    }

    async setArtifactId(artifactId: string, cellInfo: any) {
        cellInfo.setValue(artifactId || '');
        if (artifactId) {
            this.loadingPanelService.showLoadingPanelDirect('Loading config...');
            this.mcpServerDisplayGroups = await this.mcpServerService.getMcpServerDataFieldGroups(artifactId, this.currentMcpServer?.Id);
            this.loadingPanelService.hideLoadingPanel();
        }
        else {
            this.mcpServerDisplayGroups = [];
        }
    }

    getUserGroupSortValue = (server: McpServerEntity) => this.userGroupChoices.find(u => u.Id === server.AuthorizedUserGroupId)?.Name || ''

    getUserGroupDisplayValue = (server: McpServerEntity) =>
    {
        if (!server.AuthorizedUserGroupId)
            return '';
        return this.userGroupChoices.find(u => u.Id === server.AuthorizedUserGroupId)?.Name || '';
    }

    trimLeadingSlashes(value: string | null | undefined): string {
        return (value ?? '').replace(/^\/+/, '');
    }
}
